import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/state/auth';

interface JobPollingMessage {
  // Keep in sync with the worker: `UPDATE_TOKEN` re-supplies a fresh Cognito
  // token (in `authToken`) after a reactive refresh on a 401/403.
  type: 'START_POLLING' | 'UPDATE_TOKEN';
  conversationId?: string;
  apiUrl?: string;
  authToken?: string;
  account?: string;
  interval?: number;
}

interface ConversationJob {
  status?: unknown;
  [key: string]: unknown;
}

interface JobResponse {
  type: 'JOBS_UPDATE' | 'ERROR';
  data?: ConversationJob[];
  error?: string;
  conversationId?: string;
  // HTTP status for `!response.ok` errors. Present for 401/403 (auth expiry),
  // absent for network/parse failures. Kept in sync with the worker.
  status?: number;
}

// Cap how many times we reactively refresh the token in a row without an
// intervening successful poll. Normal hourly expiry needs exactly 1; a genuine
// revocation surfaces the error after 2 rather than looping forever.
const MAX_TOKEN_REFRESH_ATTEMPTS = 2;

export function useConversationJobsWorker(conversationId?: string) {
  const [jobs, setJobs] = useState<ConversationJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const workerRef = useRef<Worker | null>(null);

  // getToken/api can have unstable identities; read them from refs written
  // during render so they don't churn the worker-lifecycle effect.
  const { getToken, api, friend } = useAuth();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const apiRef = useRef(api);
  apiRef.current = api;

  // Terminate + recreate the worker whenever the conversation changes. A fresh
  // worker per conversation means: (1) the message listener always closes over
  // the CURRENT conversationId, and (2) the worker's module globals are never
  // repointed mid-life, so an in-flight poll can't be mis-stamped after a switch.
  useEffect(() => {
    if (!conversationId) {
      setJobs([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Reset stale state from the previous conversation before the first poll.
    setJobs([]);
    setError(null);
    setIsLoading(true);

    let worker: Worker;
    try {
      worker = new Worker(
        new URL('../workers/conversationJobsWorker.ts', import.meta.url),
        { type: 'module' }
      );
    } catch (err) {
      console.error('Failed to create conversation jobs worker:', err);
      setError('Worker failed to initialize');
      setIsLoading(false);
      return;
    }
    workerRef.current = worker;

    // Effect-local trackers for reactive token refresh. `let` (not refs/module
    // state) is deliberate: a fresh worker is created per [conversationId,
    // friend], so these reset naturally on recreate. `cancelled` is hoisted
    // above the listener so the async refresh closure can suppress a late post
    // after teardown.
    let cancelled = false;
    let refreshing = false;
    let authRefreshAttempts = 0;

    worker.addEventListener('message', (event: MessageEvent<JobResponse>) => {
      const {
        type,
        data,
        error: workerError,
        conversationId: responseConversationId,
        status,
      } = event.data;

      // Defense-in-depth: only process messages for the current conversation.
      if (responseConversationId !== conversationId) {
        return;
      }

      if (type === 'JOBS_UPDATE') {
        setJobs(data || []);
        setIsLoading(false);
        setError(null);
        // A successful poll means the current token is valid again — reset the
        // attempt counter so a future expiry gets a fresh refresh budget.
        authRefreshAttempts = 0;
      } else if (type === 'ERROR') {
        const isAuthError = status === 401 || status === 403;

        if (isAuthError) {
          // A refresh is already in flight — suppress so Stop/jobs stay visible.
          if (refreshing) {
            return;
          }
          // Genuine auth loss: we already refreshed the allotted times without
          // recovering. Surface the error.
          if (authRefreshAttempts >= MAX_TOKEN_REFRESH_ATTEMPTS) {
            setError(workerError || 'Authentication failed');
            setIsLoading(false);
            return;
          }

          refreshing = true;
          authRefreshAttempts += 1;
          (async () => {
            try {
              // getToken() → fetchAuthSession() auto-refreshes an expired token.
              // Side-effect to be aware of: getToken() (auth.tsx) toasts and
              // logout()s if the token is missing while signed-in. That's bounded
              // here — authRefreshAttempts caps us at MAX_TOKEN_REFRESH_ATTEMPTS
              // consecutive refreshes, so a genuine session loss logs out once,
              // not in a loop.
              const token = await getTokenRef.current();
              if (cancelled) return;
              if (token) {
                // Hand the fresh token back to the worker and deliberately do
                // NOT setError — keeping prior jobs + hasActiveJobs visible so
                // the Stop button never flickers across token expiry.
                worker.postMessage({ type: 'UPDATE_TOKEN', authToken: token });
                // isLoading is intentionally left as-is: the worker re-polls
                // immediately on UPDATE_TOKEN and that JOBS_UPDATE clears it.
                // Forcing it false here would flash an empty "no active scans"
                // state between the refresh and the re-poll; on the steady-state
                // path isLoading is already false.
              } else {
                setError('No authentication token available');
                setIsLoading(false);
              }
            } catch (err) {
              if (cancelled) return;
              setError(
                err instanceof Error ? err.message : 'Failed to refresh token'
              );
              setIsLoading(false);
            } finally {
              refreshing = false;
            }
          })();
          return;
        }

        // Non-auth error (network/parse/5xx): existing behavior.
        setError(workerError || 'Unknown error');
        setIsLoading(false);
      }
    });

    worker.addEventListener('error', err => {
      console.error('Worker error:', err);
      setError('Worker failed to initialize');
      setIsLoading(false);
    });

    (async () => {
      try {
        const token = await getTokenRef.current();
        if (cancelled) return;
        if (!token) {
          setError('No authentication token available');
          setIsLoading(false);
          return;
        }

        const message: JobPollingMessage = {
          type: 'START_POLLING',
          conversationId,
          apiUrl: apiRef.current,
          authToken: token,
          account: friend,
          interval: 3000,
        };
        worker.postMessage(message);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to initialize worker:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to initialize worker'
        );
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      worker.terminate();
      if (workerRef.current === worker) {
        workerRef.current = null;
      }
    };
    // `friend` (the impersonation target) is a real correctness dependency:
    // a tenant switch must terminate+recreate the worker so polling re-targets
    // the new partition. It's a stable primitive string, so unlike getToken/api
    // it does NOT need to be routed through a ref to avoid churn.
  }, [conversationId, friend]);

  return {
    data: jobs,
    isLoading,
    error,
  };
}
